import { z } from 'zod';

/**
 * THE VOCABULARY of the analyst in two passes (analyste.md, issue #196): the
 * three levels of the method, the three functions that carry them, the two
 * passes, and the bounds of the section a protocol writes (§3).
 *
 * ## Why this file imports nothing but Zod
 *
 * It is the base the others stand on, at the régime of `noyau.ts`: the
 * protocol's content cites a level (`analystLevel` of an objective), the stored
 * analysis cites the three of them, and the pure rules of `noyau/` read them
 * all. A module of Zod constants caught in a cycle throws at import time, so
 * the dependency runs one way, from them to here.
 *
 * ## Identifiers are English, the words are the team's
 *
 * `why`, `how`, `what` are stored; « Pourquoi », « Comment », « Quoi » are what
 * the screens say (`ANALYST_LEVEL_LABELS`). The three functions keep the letters
 * the method gives them in a triplet - S, G, P - because a triplet is read
 * letter by letter (`noyauFunctionAt`).
 */

/* --------------------------------------------------------------- the levels */

/**
 * The three LEVELS of the method (noyau.md §2.1, analyste.md §3.1), IN THE
 * ORDER OF A TRIPLET: the first letter of a noyau is the function it carries at
 * the Pourquoi, the second at the Comment, the third at the Quoi.
 *
 * The order is also the order every rule of §6 breaks its last ties in, so
 * there is one list and nobody re-spells it.
 */
export const ANALYST_LEVELS = ['why', 'how', 'what'] as const;
export const analystLevelSchema = z.enum(ANALYST_LEVELS);
export type AnalystLevel = z.infer<typeof analystLevelSchema>;

/** What each level reads as on a screen, the words of the method. */
export const ANALYST_LEVEL_LABELS: Readonly<Record<AnalystLevel, string>> = {
  why: 'Pourquoi',
  how: 'Comment',
  what: 'Quoi',
};

/**
 * The levels a sentence names, in the order of the method: « au niveau Comment »,
 * « aux niveaux Pourquoi et Quoi », « aux niveaux Pourquoi, Comment et Quoi ».
 * The line of `analyst-levels-missing` (analyste.md §3.6) says it that way on
 * every surface - the boxes of the editor and the sentences of the assistant.
 */
export function analystLevelsPhrase(levels: readonly AnalystLevel[]): string {
  const names = ANALYST_LEVELS.filter((level) => levels.includes(level)).map(
    (level) => ANALYST_LEVEL_LABELS[level],
  );
  if (names.length <= 1) return `au niveau ${names[0] ?? ''}`;
  return `aux niveaux ${names.slice(0, -1).join(', ')} et ${names.at(-1)}`;
}

/* ------------------------------------------------------------ the functions */

/**
 * The three FUNCTIONS (noyau.md §2.1; « énergies » before #196): S Souverain,
 * G Guerrier, P Producteur. Each level of a noyau is carried by one of them,
 * one of each.
 */
export const ANALYST_FUNCTIONS = ['S', 'G', 'P'] as const;
export const analystFunctionSchema = z.enum(ANALYST_FUNCTIONS);
export type AnalystFunction = z.infer<typeof analystFunctionSchema>;

/** The deposited name of each function. */
export const ANALYST_FUNCTION_NAMES: Readonly<Record<AnalystFunction, string>> = {
  S: 'Souverain',
  G: 'Guerrier',
  P: 'Producteur',
};

/* --------------------------------------------------------------- the passes */

/**
 * The two PASSES of the determination (analyste.md §5, §7): 1 reads the matter
 * objective by objective and gives the three parts of the functions, 2 reads
 * everything the person answered and notes the style of each candidate.
 *
 * Recorded on a call and on a served entry. Null on what was recorded before
 * #196, which did not have passes.
 */
export const analystPassSchema = z.union([z.literal(1), z.literal(2)]);
export type AnalystPass = z.infer<typeof analystPassSchema>;
export const ANALYST_PASSES: readonly AnalystPass[] = [1, 2];

/* ------------------------------------------------- the section of a protocol */

/**
 * The bounds of the three numbers of the section (§3.2, K2). A threshold is an
 * integer from 51 to 100 - three parts make 100, and at 50 two functions could
 * reach it together -, the style weight an integer from 0 to 100. « Atteindre »
 * counts the equality.
 */
export const ANALYST_THRESHOLD_MIN = 51;
export const ANALYST_THRESHOLD_MAX = 100;
export const ANALYST_STYLE_WEIGHT_MIN = 0;
export const ANALYST_STYLE_WEIGHT_MAX = 100;

/**
 * The number of READINGS of the style (§3.2, §7.6, issue #229): how many times the
 * second pass is read, an integer from 1 to 5, 1 by default - the analyst as it was
 * before #229, one reading, its retry, its notes.
 */
export const ANALYST_STYLE_RUNS_MIN = 1;
export const ANALYST_STYLE_RUNS_MAX = 5;
export const ANALYST_STYLE_RUNS_DEFAULT = 1;

/** What a section nobody touched says: 70 for a pair, 85 for an objective alone, half style. */
export const ANALYST_PAIR_THRESHOLD_DEFAULT = 70;
export const ANALYST_SOLO_THRESHOLD_DEFAULT = 85;
export const ANALYST_STYLE_WEIGHT_DEFAULT = 50;

/**
 * The longest reading of a pass (§3.3): 4000 characters, the bound of a
 * direction and of the reading of before #196, so a text moved from one to the
 * other always fits.
 */
export const ANALYST_READING_MAX = 4000;

/** A certainty threshold: an integer from 51 to 100. */
export const analystThresholdSchema = z
  .number()
  .int()
  .min(ANALYST_THRESHOLD_MIN)
  .max(ANALYST_THRESHOLD_MAX);

/** The weight of the style in the note of a candidate: an integer from 0 to 100. */
export const analystStyleWeightSchema = z
  .number()
  .int()
  .min(ANALYST_STYLE_WEIGHT_MIN)
  .max(ANALYST_STYLE_WEIGHT_MAX);

/** The number of readings of the style: an integer from 1 to 5 (§3.2, L3). 0, 6 and 2.5 are refused. */
export const analystStyleRunsSchema = z
  .number()
  .int()
  .min(ANALYST_STYLE_RUNS_MIN)
  .max(ANALYST_STYLE_RUNS_MAX);

/**
 * The NUMBER of one reading of the style (§10, Y2): 1 to N, the order the readings
 * are recorded in. Recorded on a call and on a served entry of the second pass when
 * the style is read several times, null on the first pass, at one reading, and on
 * what was recorded before #229.
 */
export const analystRunSchema = z
  .number()
  .int()
  .min(ANALYST_STYLE_RUNS_MIN)
  .max(ANALYST_STYLE_RUNS_MAX);
